import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import Panel from 'app/components/Panel';
import { useStore } from 'app/context';
import { formatKw } from 'app/utils';
import { Icon } from 'app/components/common/Icon';

interface Props {
	agtId: number;
	onClose: () => void;
}

interface WeekData {
	kw: number;
	status: string;
	isActive: boolean;
	inStreak: boolean;
	streakCount: number;
	month: number;
}

const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function getMonthForIsoWeek(year: number, week: number): number {
	// Get the Monday of the ISO week
	const jan4 = new Date(year, 0, 4);
	const dayOfWeek = jan4.getDay() || 7;
	const monday = new Date(jan4);
	monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
	return monday.getMonth() + 1;
}

export const AgtLeadNowConsecutiveDetail = observer((props: Props) => {
	const { agtId, onClose } = props;
	const { agtLeadNowStore, agtLeadNowConsecutiveStore } = useStore();
	const [weeks, setWeeks] = useState<WeekData[]>([]);
	const [consecutiveCount, setConsecutiveCount] = useState(0);
	const [firstKw, setFirstKw] = useState(0);
	const [lastKw, setLastKw] = useState(0);
	const [maxKw, setMaxKw] = useState(0);

	useEffect(() => {
		Promise.all([agtLeadNowStore.loadWeeklyByAgt(agtId), agtLeadNowConsecutiveStore.loadForCurrentYear()]).then(([weeklyItems, consecutiveItems]) => {
			const dataByKw = new Map(weeklyItems.map((item) => [item.kw, item]));
			const year = new Date().getFullYear();

			const allKws: number[] = [];
			for (let kw = year * 100 + 1; kw <= year * 100 + 53; kw++) allKws.push(kw);

			// Pass 1: assign group id per kw — only explicit non-active rows increment the group
			// (mirrors the SQL view's `grp` window: SUM(CASE WHEN status NOT IN ('a','v') THEN 1 ELSE 0 END)).
			// Missing rows (gaps) do not appear in the window, so they don't bump the group.
			const groupByKw = new Map<number, number>();
			let currentGroup = 0;
			for (const kw of allKws) {
				const item = dataByKw.get(kw);
				if (item && item.agenturstatus !== 'a' && item.agenturstatus !== 'v') {
					currentGroup++;
				}
				groupByKw.set(kw, currentGroup);
			}

			// Pass 2: per group, find the first and last *active* kw — trailing gaps are trimmed.
			const groupBounds = new Map<number, { firstActive: number; lastActive: number }>();
			for (const kw of allKws) {
				const item = dataByKw.get(kw);
				if (!item || (item.agenturstatus !== 'a' && item.agenturstatus !== 'v')) continue;
				const grp = groupByKw.get(kw)!;
				const b = groupBounds.get(grp);
				if (!b) groupBounds.set(grp, { firstActive: kw, lastActive: kw });
				else if (kw > b.lastActive) b.lastActive = kw;
			}

			const points: WeekData[] = [];
			for (const kw of allKws) {
				const weekNum = kw % 100;
				const item = dataByKw.get(kw);
				const status = item ? item.agenturstatus : '';
				const isActive = status === 'a' || status === 'v';
				const bounds = groupBounds.get(groupByKw.get(kw)!);
				const inStreak = !!bounds && kw >= bounds.firstActive && kw <= bounds.lastActive;
				const streakCount = inStreak ? (kw % 100) - (bounds!.firstActive % 100) + 1 : 0;
				points.push({ kw, status, isActive, inStreak, streakCount, month: getMonthForIsoWeek(year, weekNum) });
			}

			const agtData = consecutiveItems.find((m) => m.vtnrId === agtId);
			setConsecutiveCount(agtData?.consecutiveCount ?? 0);
			setFirstKw(agtData?.firstKw ?? 0);
			setLastKw(agtData?.lastKw ?? 0);

			const mk = weeklyItems.length > 0 ? Math.max(...weeklyItems.map((i) => i.kw)) : 0;
			setMaxKw(mk);
			setWeeks(points);
		});
	}, [agtLeadNowStore, agtLeadNowConsecutiveStore, agtId]);

	const monthSpans = useMemo(() => {
		if (weeks.length === 0) return [];
		const spans: { month: number; count: number }[] = [];
		let current = weeks[0].month;
		let count = 1;
		for (let i = 1; i < weeks.length; i++) {
			if (weeks[i].month === current) {
				count++;
			} else {
				spans.push({ month: current, count });
				current = weeks[i].month;
				count = 1;
			}
		}
		spans.push({ month: current, count });
		return spans;
	}, [weeks]);

	return (
		<Panel title="LeadNow Nutzung 2026" subtitle={`Agentur: ${agtId}`} onClose={onClose} header={<></>} classModifier="is-leadnow-detail">
			{weeks.length > 0 ? (
				<div className="pad-1rem" style={{ overflowX: 'auto' }}>
					<table className="table is-narrow is-fullwidth" style={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
						<thead>
							<tr>
								{monthSpans.map((span, idx) => (
									<th
										key={idx}
										colSpan={span.count}
										style={{
											textAlign: 'center',
											fontSize: 11,
											padding: '4px 0',
											borderLeft: idx > 0 ? '2px solid #ccc' : undefined,
											fontWeight: 'bold',
										}}
									>
										{MONTH_NAMES[span.month]}
									</th>
								))}
							</tr>
							<tr>
								{weeks.map((w, idx) => {
									const isMonthBorder = idx > 0 && weeks[idx - 1].month !== w.month;
									return (
										<th
											key={w.kw}
											style={{
												textAlign: 'center',
												fontSize: 10,
												padding: '2px 1px',
												background: w.kw === maxKw ? '#2196F3' : undefined,
												color: w.kw === maxKw ? '#fff' : undefined,
												borderLeft: isMonthBorder ? '2px solid #ccc' : undefined,
											}}
										>
											{w.kw % 100}
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							<tr>
								{weeks.map((w, idx) => {
									const isMonthBorder = idx > 0 && weeks[idx - 1].month !== w.month;
									const background = w.kw === maxKw
										? '#e3f2fd'
										: w.isActive
											? '#e8f5e9'
											: w.inStreak
												? '#f1f8e9'
												: undefined;
									const color = w.isActive ? '#2e7d32' : w.inStreak ? '#9e9e9e' : '#ccc';
									return (
										<td
											key={w.kw}
											style={{
												textAlign: 'center',
												padding: '2px 1px',
												fontSize: 11,
												fontWeight: w.isActive ? 'bold' : 'normal',
												fontStyle: w.inStreak && !w.isActive ? 'italic' : undefined,
												background,
												color,
												borderLeft: isMonthBorder ? '2px solid #ccc' : undefined,
											}}
										>
											{w.inStreak ? w.streakCount : ''}
										</td>
									);
								})}
							</tr>
						</tbody>
					</table>
				</div>
			) : (
				<div className="pad-1rem">Keine Daten vorhanden</div>
			)}

			<div className="pad-1rem">
				<div className="note-box content margin-bottom-1rem">
					<p>
						<Icon iconClass="info-circle" />
					</p>
					<div className="note-message">
						<p>
							<strong>Hinweis</strong>
						</p>
						<ul>
							<li>
								Max. konsekutive Wochen (Status a/v): <strong>{consecutiveCount}</strong>
								{firstKw > 0 && lastKw > 0 && (
									<>
										{' '}
										(KW {formatKw(firstKw)} - {formatKw(lastKw)})
									</>
								)}
							</li>
							<li>GP Ziel: 12 konsekutive Wochen mit Status Aktiv oder Urlaub</li>
							{maxKw > 0 && <li>Letzter Datenstand: KW {formatKw(maxKw)}</li>}
						</ul>
					</div>
				</div>
			</div>
		</Panel>
	);
});
